<script lang="ts">
import type { SortDirection } from '#layers/base/public';
import type { GlobalPrimitiveFieldId, QuerySchemaWalker, ResolvedMonospaceCollection, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { CardsDisplayOptions } from './LayoutCards.vue';
import { CollectionFieldSelect, CollectionReads, DisplayTemplateInput, isToOneFileRelation, resolveCollectionDisplayTemplate, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { useInitializedDisplayOptionsModel } from '#layers/listing/composables/use-initialized-display-options-model';
import { initialSortState } from '#layers/listing/utils/initial-sort-state';

function guessCollectionThumbnailField(collection: ResolvedMonospaceCollection) {
	return collection.relationFields.find((field) => isToOneFileRelation(field));
}

interface InitializeLayoutCardsOptionsParams {
	collection: ResolvedMonospaceCollection;
	options?: Partial<CardsDisplayOptions> | null;
	sortDirections: (fieldId: GlobalPrimitiveFieldId) => SortDirection[];
	/** The workspace query schema, which decides the collection's display template, see `resolveCollectionDisplayTemplate`. */
	querySchema: QuerySchemaWalker | null | undefined;
}

export function initializeLayoutCardsOptions({ collection, options, sortDirections, querySchema }: InitializeLayoutCardsOptionsParams): CardsDisplayOptions {
	return {
		sorting: initialSortState(collection, sortDirections),
		...options,
		titleTemplate: options?.titleTemplate ?? resolveCollectionDisplayTemplate(collection, CollectionReads.Many, querySchema),
		descriptionTemplate: options?.descriptionTemplate ?? null,
		thumbnailField: options?.thumbnailField ?? guessCollectionThumbnailField(collection)?.id ?? null,
		cardSize: options?.cardSize ?? 'md',
	};
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import LayoutDisplayOptionSort from '#layers/listing/components/Layout/DisplayOption/LayoutDisplayOptionSort.vue';
import { useSortGate } from '#layers/listing/composables/use-sort-gate';

interface CardsDisplayOptionProps {
	collection: ResolvedMonospaceCollection;
	displayOptions?: CardsDisplayOptions;
	sharedDisplayOptions?: CardsDisplayOptions;
}

const { collection, sharedDisplayOptions, displayOptions } = defineProps<CardsDisplayOptionProps>();
const emit = defineEmits<{
	'update:displayOptions': [value: CardsDisplayOptions | undefined];
}>();

const sortGate = useSortGate(() => collection);
const workspaceSchema = useWorkspaceSchemaStore();

const model = useInitializedDisplayOptionsModel(
	{
		baselineDisplayOptions: () => sharedDisplayOptions,
		displayOptions: () => displayOptions,
		initializedBaselineOptions: () => initializeLayoutCardsOptions({ collection, options: sharedDisplayOptions, sortDirections: sortGate.directions, querySchema: workspaceSchema.querySchema }),
		initializedOptions: () => initializeLayoutCardsOptions({ collection, options: displayOptions, sortDirections: sortGate.directions, querySchema: workspaceSchema.querySchema }),
	},
	(value) => emit('update:displayOptions', value),
);

const collectionDisplayTemplate = computed(() => resolveCollectionDisplayTemplate(collection, CollectionReads.Many, workspaceSchema.querySchema));

function isThumbnailField(field: ResolvedMonospaceSingleRelationField) {
	return isToOneFileRelation(field);
}
</script>

<template>
	<template v-if="model">
		<LayoutDisplayOptionSort v-model="model.sorting" :collection />
		<DFormSeparator class="-mx-4 my-1" />
		<DFormField :label="$t('common.title')">
			<DisplayTemplateInput
				v-model="model.titleTemplate"
				:default-value="collectionDisplayTemplate"
				:collection
				:aria-label="$t('common.title')"
			/>
		</DFormField>
		<DFormField :label="$t('common.description')">
			<DisplayTemplateInput
				v-model="model.descriptionTemplate"
				:collection
				:aria-label="$t('common.description')"
			/>
		</DFormField>
		<DFormField :label="$t('common.thumbnail')">
			<CollectionFieldSelect
				v-model="model.thumbnailField"
				:include-primitive-fields="false"
				:collection
				:filter="isThumbnailField"
			/>
		</DFormField>
		<DFormField :label="$t('layout.cards.card_size.label')">
			<DSelect
				v-model="model.cardSize"
				:items="[
					{ label: $t('layout.cards.card_size.options.small'), value: 'sm' },
					{ label: $t('layout.cards.card_size.options.medium'), value: 'md' },
					{ label: $t('layout.cards.card_size.options.large'), value: 'lg' },
				]"
			/>
		</DFormField>
	</template>
</template>
